import {
  connectWaitingRoomWebSocket,
  disconnectWaitingRoomWebSocket,
} from '@/redux/asyncThunks/websocketThunks'
import { getRoomDetails } from '@/redux/asyncThunks/room'
import {
  fetchCharterSession,
  fetchMyProfile,
} from '@/features/charter/redux/asyncThunks/charterThunks'
import { useAppDispatch } from '@/redux/hooks'
import { RootState } from '@/redux/store'
import { GameRoomStatus } from '@/redux/types/room'
import ROUTES from '@/routes/routes'
import { getAccessToken } from '@/utils/authUtils'
import { useMyPrepStatus } from '@/features/charter/hooks/useMyPrepStatus'
import { PreTeamPrepCard } from '@/features/charter/components/Shared/PreTeamPrepCard'
import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

const instructions = [
  'When the Game begins, you will enter a briefing room.  Please interrogate the reference and research AI engine to learn more about the scenario and historical background before the game begins.',
  'After 15 minutes, the game will begin. You will be presented with the scenario and begin consulting your Advisors about what decisions should be made.',
]

const gameStatus = 'Waiting to Begin'

const WaitingRoom = () => {
  const params = useParams()
  const roomId = params.roomId as string
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const inviteCode = searchParams.get('inviteCode')

  const { isConnected, waitingRoomStatus } = useSelector(
    (state: RootState) => state.waitingRoomWS
  )
  const { currentRoom } = useSelector((state: RootState) => state.room)
  const charterSession = useSelector(
    (state: RootState) => state.charter.session
  )
  const isSimulating = currentRoom?.is_simulating === true
  const charterEnabled = currentRoom?.charter_enabled === true
  const jwtToken = getAccessToken()
  // Prep status answers before placement; the session endpoint 404s until then.
  const prepStatus = useMyPrepStatus(roomId, charterEnabled && !isSimulating && !charterSession)
  const awaitingTeam = charterEnabled && !isSimulating && prepStatus?.has_team === false
  const placed = prepStatus?.has_team === true

  useEffect(() => {
    if (roomId) {
      dispatch(getRoomDetails(roomId))
    }
  }, [dispatch, roomId])

  useEffect(() => {
    if (jwtToken && !isConnected && inviteCode) {
      dispatch(connectWaitingRoomWebSocket({ jwtToken, roomId, inviteCode }))
    }

    return () => {
      if (isConnected) {
        dispatch(disconnectWaitingRoomWebSocket())
      }
    }
  }, [dispatch, jwtToken, isConnected, roomId, inviteCode])

  useEffect(() => {
    if (
      !isSimulating &&
      (waitingRoomStatus === GameRoomStatus.IN_PROGRESS ||
        waitingRoomStatus === GameRoomStatus.COMPLETED ||
        waitingRoomStatus === GameRoomStatus.PAUSED) &&
      inviteCode
    ) {
      navigate(ROUTES.STUDENT.GAME(roomId, inviteCode))
    }
  }, [waitingRoomStatus, isSimulating, navigate, inviteCode, roomId])

  // Load own prep profile once there is one (the endpoint 404s before the
  // student opens prep), and the session once they are on a team (being placed
  // pushes nothing here).
  const hasPrep = Boolean(prepStatus?.prep)
  useEffect(() => {
    if (charterEnabled && roomId && hasPrep) {
      dispatch(fetchMyProfile({ roomId }))
    }
  }, [dispatch, charterEnabled, roomId, hasPrep])

  useEffect(() => {
    if (charterEnabled && roomId && placed) {
      dispatch(fetchCharterSession({ roomId }))
    }
  }, [dispatch, charterEnabled, roomId, placed])

  // Charter routing: the charter flow (prep + group) now runs entirely inside
  // the unified game shell (PlayerGame). As soon as the charter session is
  // known, send the student straight into the game screen — it renders the
  // charter tabs + sidebar pre-game and the full game UI once it starts.
  useEffect(() => {
    if (isSimulating || !charterEnabled || !inviteCode || !charterSession) {
      return
    }
    navigate(ROUTES.STUDENT.GAME(roomId, inviteCode))
  }, [
    charterEnabled,
    charterSession,
    isSimulating,
    inviteCode,
    roomId,
    navigate,
  ])

  return (
    <div className='min-h-full pt-2 pl-[5.75rem]'>
      <div className='w-1/2 space-y-4'>
        <div className='px-1 py-2'>
          <h1 className='text-2xl font-bold text-dark-blue'>
            Room ID: {roomId}
          </h1>
        </div>
        <div className='w-1/2 p-1 rounded-lg bg-[#E6ECF2] flex gap-4'>
          <span className='text-xl font-bold text-dark-blue'>Game Status</span>
          <div className='flex items-center gap-2'>
            <span className='text-xl font-bold text-dark-blue'>
              {gameStatus}
            </span>
            <img src='/icons/clock-red.svg' alt='Clock' className='w-6 h-6' />
          </div>
        </div>

        {awaitingTeam && (
          <PreTeamPrepCard roomId={roomId} inviteCode={inviteCode ?? ''} status={prepStatus} />
        )}

        <div className='p-[0.0625rem] bg-gradient-to-bl from-[#1E4C90] to-[#D0D5DA] rounded-tl-lg rounded-br-lg shadow-[0px_2px_2px_0px_rgba(0,0,0,0.09)]'>
          <div className='space-y-4 bg-white rounded-tl-lg rounded-br-lg'>
            <h2 className='p-1 text-xl font-bold text-dark-blue'>
              Game Instructions
            </h2>
            <ul className="text-lg text-black font-normal p-1 space-y-3 list-image-[url('../icons/triangle-red.svg')] list-inside">
              {instructions.length > 0 ? (
                instructions.map((instruction, index) => (
                  <li key={index}>{instruction}</li>
                ))
              ) : (
                <li>Loading...</li>
              )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WaitingRoom
